import auth0 from '@/auth/auth0Plugin';
import { captureError } from '@smg-real-estate/browser-rum';

export interface UnreadMessageCount {
  smg_listing: string;
  count: number;
  created: string | null;
}

export interface BatchUnreadCountsResponse {
  data: UnreadMessageCount[];
}

export class FlatfoxApiError extends Error {
  endpoint: string;

  constructor(endpoint: string, message: string) {
    super(`Flatfox API error: ${message}`);
    this.name = 'FlatfoxApiError';
    this.endpoint = endpoint;
  }
}

export async function getBatchUnreadMessageCounts(
  listingIds: string[],
  customerId: string,
): Promise<UnreadMessageCount[]> {
  if (!listingIds.length) {
    return [];
  }

  if (!customerId) {
    throw new FlatfoxApiError('', 'No active agency ID available');
  }

  // Request token with Flatfox audience to ensure proper API authorization
  const authToken = await auth0.getAccessTokenSilently({
    authorizationParams: {
      audience: import.meta.env.VUE_FLATFOX_URL,
    },
  });

  const idsQueryParam = listingIds.join(',');
  const url = `${
    import.meta.env.VUE_FLATFOX_URL
  }/api/v1/unread-state/groupby/smg_listing/?smg_listing_ids=${encodeURIComponent(
    idsQueryParam,
  )}&role=advertiser`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Flatfox-Org-Switcher-Smg-Customer': customerId,
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    captureError(new FlatfoxApiError(url, errorMessage));
    throw new FlatfoxApiError(url, errorMessage);
  }
}
